import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import Label from './Label';

export type SelectInputOption = {
  isDisabled?: boolean;
  label: React.ReactNode;
  value: number | string;
};

type Props<T extends FieldValues> = UseControllerProps<T> &
  Pick<SelectProps, 'displayEmpty' | 'MenuProps' | 'renderValue'> & {
    isDisabled?: boolean;
    isRequired?: boolean;
    label?: React.ReactNode;
    manualError?: string;
    options: SelectInputOption[];
    suppressErrorMessage?: boolean;
    sx?: SxProps<Theme>;
  };

export default function SelectInput<T extends FieldValues>({
  control,
  displayEmpty,
  isDisabled,
  isRequired,
  label,
  manualError,
  MenuProps,
  name,
  options,
  renderValue,
  suppressErrorMessage,
  sx,
}: Props<T>) {
  const { field, fieldState } = useController({ control, name });
  const error =
    manualError ?? fieldState.error?.message ?? fieldState.error?.root?.message;
  const value = field.value ?? '';

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <Select
        MenuProps={MenuProps}
        className={isRequired ? 'required' : ''}
        disabled={isDisabled}
        displayEmpty={displayEmpty}
        error={Boolean(error)}
        fullWidth
        id={name}
        onBlur={field.onBlur}
        onChange={(event) => field.onChange(event.target.value)}
        renderValue={renderValue}
        size="small"
        value={value}
      >
        {options.map((option) => (
          <MenuItem
            key={`${name}-${String(option.value)}`}
            disabled={option.isDisabled}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {error && !suppressErrorMessage && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
